import type { TenantAccess } from '@konyaxl/shared';
import { useEffect, useRef, useState } from 'react';
import { BarkodsuzPage } from '../components/BarkodsuzPage.js';
import { ImageScanPage } from '../components/ImageScanPage.js';
import { ScanPage } from '../components/ScanPage.js';
import { useAuth } from '../core/auth.js';
import {
  exitApp,
  hasHardwareScanner,
  onNativeBack,
  onSharedImage,
  takeSharedImage,
} from '../core/native.js';
import { DashboardPage } from './DashboardPage.js';
import { KayitlarPage } from './KayitlarPage.js';
import { ReferansPage } from './ReferansPage.js';
import { ExpectedPage } from './ExpectedPage.js';

type Gorunum =
  | {
      ad: 'kayitlar' | 'tara' | 'beklenen' | 'resim' | 'panel' | 'referans';
      tenant: TenantAccess;
    }
  | null;

/**
 * Masaüstü ana ekranı: kullanıcının yetkili olduğu kategoriler.
 *
 * Eski projede bu liste koda gömülüydü ve her kategori için ayrı bir
 * `Card` bloğu yazılmıştı (homepage.dart:120-157). Burada sunucudan gelen
 * listeden üretiliyor — yeni kategori kod değişikliği gerektirmiyor.
 */
export function HomePage() {
  const { user, tenants, logout } = useAuth();
  const [gorunum, setGorunum] = useState<Gorunum>(null);
  const [paylasilanResim, setPaylasilanResim] = useState<Blob | null>(null);
  const [geriUyarisi, setGeriUyarisi] = useState(false);
  const sonGeri = useRef(0);

  /**
   * WhatsApp'tan paylaşılan resim.
   *
   * Kullanıcı zaten bir kategoriyle çalışıyorsa o kategoriye, değilse ilk
   * yetkili kategoriye açılır — paylaşım anında kategori sormak akışı keser.
   */
  useEffect(() => {
    let iptal = false;

    const isle = async () => {
      // Kategori yoksa resmi almaya kalkma: alınan resim kuyruktan düşer
      // ve gösterilemeden kaybolurdu.
      const hedef = tenants[0];
      if (!hedef) return;

      const resim = await takeSharedImage();
      if (iptal || !resim) return;

      setPaylasilanResim(resim);
      setGorunum({ ad: 'resim', tenant: hedef });
    };

    // Uygulama paylaşımla açıldıysa resim zaten bekliyordur.
    void isle();

    const durdur = onSharedImage(() => void isle());
    return () => {
      iptal = true;
      durdur();
    };
  }, [tenants]);

  useEffect(() => {
    const durdur = onNativeBack(() => {
      if (gorunum) {
        setPaylasilanResim(null);
        setGorunum(null);
        setGeriUyarisi(false);
        return;
      }

      const simdi = Date.now();
      if (simdi - sonGeri.current < 1600) {
        void exitApp();
        return;
      }

      sonGeri.current = simdi;
      setGeriUyarisi(true);
      setTimeout(() => setGeriUyarisi(false), 1600);
    });

    return durdur;
  }, [gorunum]);

  if (gorunum?.ad === 'tara') {
    // Eşleştirmesiz kategorilerde tarama ekranı değil, tek kayıt formu açılır.
    return gorunum.tenant.features.singleEntry ? (
      <BarkodsuzPage tenant={gorunum.tenant} onBack={() => setGorunum(null)} />
    ) : (
      <ScanPage tenant={gorunum.tenant} onBack={() => setGorunum(null)} />
    );
  }

  if (gorunum?.ad === 'kayitlar') {
    return <KayitlarPage tenant={gorunum.tenant} onBack={() => setGorunum(null)} />;
  }

  if (gorunum?.ad === 'resim') {
    return (
      <ImageScanPage
        tenant={gorunum.tenant}
        baslangicResmi={paylasilanResim}
        onBack={() => {
          setPaylasilanResim(null);
          setGorunum(null);
        }}
      />
    );
  }

  if (gorunum?.ad === 'panel') {
    return <DashboardPage tenant={gorunum.tenant} onBack={() => setGorunum(null)} />;
  }

  if (gorunum?.ad === 'referans') {
    return <ReferansPage tenant={gorunum.tenant} onBack={() => setGorunum(null)} />;
  }

  if (gorunum?.ad === 'beklenen') {
    return <ExpectedPage tenant={gorunum.tenant} onBack={() => setGorunum(null)} />;
  }

  return (
    <>
      <header className="ust-bar">
        <h1>KonyaXL</h1>
        <div className="ust-bar-sag">
          <span>
            {user?.fullName}
            {user?.role === 'admin' && ' · yönetici'}
          </span>
          <button className="btn-cikis" onClick={() => void logout()}>
            Çıkış
          </button>
        </div>
      </header>

      <main className="icerik">
        {geriUyarisi && <div className="geri-uyari">Çıkmak için tekrar geri basın</div>}

        <p className="baslik">
          {tenants.length > 0
            ? 'Kategori seçin'
            : 'Hesabınıza tanımlı kategori bulunmuyor'}
        </p>

        {tenants.length === 0 ? (
          <div className="bos-durum">
            Henüz bir kategoriye yetkiniz yok.
            <br />
            Yöneticinizden erişim talep edin.
          </div>
        ) : (
          <div className="kart-liste">
            {tenants.map((t) => (
              <div key={t.id} className="kategori-kart">
                <h3>{t.name}</h3>
                <div style={{ fontSize: 13, color: 'var(--metin-soluk)' }}>
                  {/* Eşleştirmesiz kategoride ikinci kod yok; ok işareti
                      boşluğa işaret ediyordu. */}
                  {t.features.singleEntry
                    ? t.scanRules.a.label
                    : `${t.scanRules.a.label} ↔ ${t.scanRules.b.label}`}
                </div>
                <span className="rozet">
                  {t.canWrite ? 'Okuma ve kayıt' : 'Yalnızca okuma'}
                </span>

                <div className="kart-butonlar">
                  {!hasHardwareScanner() && (
                    <>
                      {/* Ekranlar kategoriye göre açılıyor: her müşteri aynı
                          işi yapmıyor, kullanılmayan ekran operatörü yanıltır. */}
                      {t.features.dashboard && (
                        <button
                          className="btn-kart"
                          onClick={() => setGorunum({ ad: 'panel', tenant: t })}
                        >
                          Gösterge paneli
                        </button>
                      )}
                      <button
                        className="btn-kart"
                        onClick={() => setGorunum({ ad: 'kayitlar', tenant: t })}
                      >
                        Kayıtlar ve arama
                      </button>
                      {/* Referans listesi bilgisayarda yükleniyor: veri başka
                          bir sistemden kopyalanıp yapıştırılıyor. */}
                      {t.canWrite && t.matchRule.type === 'reference' && (
                        <button
                          className="btn-kart"
                          onClick={() => setGorunum({ ad: 'referans', tenant: t })}
                        >
                          Referans listesi
                        </button>
                      )}
                      {t.canWrite && t.features.expectedList && (
                        <button
                          className="btn-kart"
                          onClick={() => setGorunum({ ad: 'beklenen', tenant: t })}
                        >
                          Beklenen liste
                        </button>
                      )}
                    </>
                  )}
                  {t.canWrite && (
                    <button
                      className="btn-kart birincil"
                      onClick={() => setGorunum({ ad: 'tara', tenant: t })}
                    >
                      {t.features.singleEntry ? 'Ürün ekle' : 'Barkod tara'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </>
  );
}
